import type { TsoaRoute } from '@tsoa/runtime';
import type { RequestHandler } from 'express';
import { isToolMethod, getToolRouteClass, getMCPRouteMetadata, type BaseMCPRoute } from './decorator.js';

/**
 * A single route entry from the generated tsoaRouteRegistry
 */
export interface TsoaRouteEntry {
  controllerName: string;
  methodName: string;
  method: string;
  path: string;
  security: TsoaRoute.Security[];
  args: Record<string, TsoaRoute.ParameterSchema>;
  getMiddlewares: () => RequestHandler[];
  /** Standard Express invoke (used by RegisterRoutes) */
  invoke: (request: any, response: any, next: any) => Promise<void>;
  /** Direct invoke — calls the controller method and returns the result, no res/next needed */
  invokeDirect: (request: any) => Promise<any>;
  controllerClass: new (...args: any[]) => any;
}

/**
 * A resolved MCP tool derived from a TSOA route entry + MCPRoute class
 */
export interface McpToolEntry {
  /** Tool name (from @MCPRoute) */
  name: string;
  /** Tool description (from @MCPRoute) */
  description: string;
  /** Controller class name */
  controllerName: string;
  /** Controller method name */
  methodName: string;
  /** HTTP method */
  method: string;
  /** Route path */
  path: string;
  /** TSOA parameter schema for building the input JSON schema */
  args: Record<string, TsoaRoute.ParameterSchema>;
  /** Security requirements */
  security: TsoaRoute.Security[];
  /** Get the middleware chain for this route */
  getMiddlewares: () => RequestHandler[];
  /** Invoke the controller method with validated args */
  invoke: TsoaRouteEntry['invoke'];
  /** Direct invoke — returns the controller result directly */
  invokeDirect: TsoaRouteEntry['invokeDirect'];
  /** The instantiated MCPRoute resolver */
  resolver: BaseMCPRoute;
  /** OAuth mode: 'required' = hidden without OAuth, 'optional' = cookie params exposed, false = no OAuth */
  oauth: import('./decorator.js').MCPOAuthMode;
}

/**
 * Filters a tsoaRouteRegistry down to only entries decorated with @Tool,
 * instantiates their MCPRoute classes, and resolves metadata.
 */
export function resolveTools(registry: TsoaRouteEntry[]): McpToolEntry[] {
  const tools: McpToolEntry[] = [];

  for (const entry of registry) {
    const method = entry.controllerClass.prototype[entry.methodName];
    if (!method || !isToolMethod(method)) continue;

    const routeClass = getToolRouteClass(method);
    if (!routeClass) continue;

    const meta = getMCPRouteMetadata(routeClass);
    if (!meta) continue;

    // Instantiate the MCPRoute once
    const resolver = new routeClass();

    tools.push({
      name: meta.name,
      description: meta.description || `${entry.method.toUpperCase()} ${entry.path}`,
      controllerName: entry.controllerName,
      methodName: entry.methodName,
      method: entry.method,
      path: entry.path,
      args: entry.args,
      security: entry.security,
      getMiddlewares: entry.getMiddlewares,
      invoke: entry.invoke,
      invokeDirect: entry.invokeDirect,
      resolver,
      oauth: meta.oauth,
    });
  }

  return tools;
}

/**
 * Build a JSON Schema "properties" object from TSOA parameter definitions.
 * Used to define the MCP tool's inputSchema.
 */
export function buildInputSchema(
  args: Record<string, TsoaRoute.ParameterSchema>,
  options?: { includeHeaders?: boolean }
): {
  type: 'object';
  properties: Record<string, any>;
  required: string[];
} {
  const properties: Record<string, any> = {};
  const required: string[] = [];
  const includeHeaders = options?.includeHeaders ?? false;

  for (const [key, param] of Object.entries(args)) {
    // Skip the raw request object — it's not a user-facing parameter
    if (param.in === 'request') continue;
    if (param.in === 'header' && !includeHeaders) continue;

    const prop: Record<string, any> = {};

    switch (param.dataType) {
      case 'string':
        prop.type = 'string';
        break;
      case 'double':
      case 'float':
      case 'integer':
      case 'long':
        prop.type = 'number';
        break;
      case 'boolean':
        prop.type = 'boolean';
        break;
      case 'array':
        prop.type = 'array';
        break;
      case 'object':
        prop.type = 'object';
        break;
      default:
        if (param.ref) {
          prop.type = 'object';
        } else {
          prop.type = 'string';
        }
    }

    if ('default' in param) {
      prop.default = param.default;
    }

    properties[key] = prop;

    if (param.required) {
      required.push(key);
    }
  }

  return { type: 'object', properties, required };
}
